import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, RefreshCw, Pin, Pencil } from 'lucide-react';
import { CustomerIndividualDeadline } from '../../../shared/types';
import { customerIndividualDeadlineService } from '../../../services/customer/CustomerIndividualDeadlineService';
import { supabase } from '../../../shared/services/supabase';

interface IndividualDeadlinesManagerProps {
  customerId: string;
  onUpdate?: () => void;
}

export const IndividualDeadlinesManager: React.FC<IndividualDeadlinesManagerProps> = ({
  customerId,
  onUpdate
}) => {
  const [deadlines, setDeadlines] = useState<CustomerIndividualDeadline[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDeadlineId, setEditingDeadlineId] = useState<string | null>(null);
  const [formType, setFormType] = useState<'recurring' | 'one-time'>('recurring');

  // Verfuegbare Lohnlaeufe des Kunden (fuer das Anker-Dropdown relativer Fristen).
  // Wird lokal anhand der customerId nachgeladen; nur tatsaechlich vorhandene
  // Lohnlaeufe werden als Bezug angeboten (verhindert Fehleingaben).
  const [availableLohnlaeufe, setAvailableLohnlaeufe] = useState<Array<{ value: 1 | 2; label: string }>>([]);
  
  // Form states
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    // Für wiederkehrende (fester Tag)
    dayOfMonth: 15,
    monatOffset: 0,
    verschiebung: null as 'frueher' | 'spaeter' | null,
    // Für wiederkehrende (relativ zum Lohnlauf)
    bezugsart: 'fest' as 'fest' | 'relativ',
    bezugLohnlauf: 1 as 1 | 2,
    bezugWerktageBetrag: 1,
    bezugRichtung: 'danach' as 'davor' | 'danach' | 'am_tag',
    // Für einmalige
    specificDate: new Date().toISOString().split('T')[0]
  });

  const resetFormData = () => {
    setFormData({
      title: '',
      description: '',
      dayOfMonth: 15,
      monatOffset: 0,
      verschiebung: null,
      bezugsart: 'fest',
      bezugLohnlauf: 1,
      bezugWerktageBetrag: 1,
      bezugRichtung: 'danach',
      specificDate: new Date().toISOString().split('T')[0]
    });
  };

  const loadDeadlines = useCallback(async () => {
    setLoading(true);
    try {
      const data = await customerIndividualDeadlineService.getCustomerIndividualDeadlines(customerId);
      setDeadlines(data);
    } catch (error) {
      console.error('Error loading deadlines:', error);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    loadDeadlines();
  }, [loadDeadlines]);

  // Lohnlauf-Konfiguration des Kunden laden, damit das Anker-Dropdown nur die
  // tatsaechlich vorhandenen Lohnlaeufe anbietet.
  useEffect(() => {
    let cancelled = false;
    const loadLohnlaeufe = async () => {
      const { data } = await supabase
        .from('customers')
        .select('erster_lohnlauf_tag, zweiter_lohnlauf_tag')
        .eq('id', customerId)
        .maybeSingle();
      if (cancelled || !data) return;
      const options: Array<{ value: 1 | 2; label: string }> = [];
      if (data.erster_lohnlauf_tag) options.push({ value: 1, label: '1. Lohnlauf' });
      if (data.zweiter_lohnlauf_tag) options.push({ value: 2, label: '2. Lohnlauf' });
      setAvailableLohnlaeufe(options);
    };
    loadLohnlaeufe();
    return () => { cancelled = true; };
  }, [customerId]);

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      alert('Bitte geben Sie einen Titel ein');
      return;
    }

    try {
      if (formType === 'recurring') {
        if (formData.bezugsart === 'relativ') {
          const offset = formData.bezugRichtung === 'am_tag'
            ? 0
            : formData.bezugRichtung === 'danach'
              ? formData.bezugWerktageBetrag
              : -formData.bezugWerktageBetrag;
          await customerIndividualDeadlineService.createRelativeRecurringDeadline(
            customerId,
            formData.title,
            formData.description || null,
            formData.bezugLohnlauf,
            offset
          );
        } else {
          await customerIndividualDeadlineService.createRecurringDeadline(
            customerId,
            formData.title,
            formData.description || null,
            formData.dayOfMonth,
            formData.monatOffset,
            formData.verschiebung
          );
        }
      } else {
        await customerIndividualDeadlineService.createOneTimeDeadline(
          customerId,
          formData.title,
          formData.description || null,
          formData.specificDate
        );
      }

      // Reset form
      resetFormData();
      setShowAddForm(false);
      
      await loadDeadlines();
      onUpdate?.();
    } catch (error) {
      console.error('Error creating deadline:', error);
      alert('Fehler beim Erstellen der Deadline');
    }
  };

  const handleEdit = (deadline: CustomerIndividualDeadline) => {
    // Add-Formular schließen falls offen
    setShowAddForm(false);
    
    // Formular mit bestehenden Werten füllen
    setEditingDeadlineId(deadline.id);
    setFormType(deadline.is_recurring ? 'recurring' : 'one-time');
    const istRelativ = !!(deadline.is_recurring && deadline.bezug_lohnlauf);
    const offset = deadline.bezug_werktage_offset ?? 0;
    setFormData({
      title: deadline.title,
      description: deadline.description || '',
      dayOfMonth: deadline.day_of_month || 15,
      monatOffset: deadline.monat_offset || 0,
      verschiebung: deadline.verschiebung || null,
      bezugsart: istRelativ ? 'relativ' : 'fest',
      bezugLohnlauf: (deadline.bezug_lohnlauf as 1 | 2) || 1,
      bezugWerktageBetrag: Math.max(1, Math.abs(offset) || 1),
      bezugRichtung: offset === 0 ? 'am_tag' : offset < 0 ? 'davor' : 'danach',
      specificDate: deadline.specific_date 
        ? new Date(deadline.specific_date).toISOString().split('T')[0] 
        : new Date().toISOString().split('T')[0]
    });
  };

  const handleUpdate = async () => {
    if (!editingDeadlineId) return;
    
    if (!formData.title.trim()) {
      alert('Bitte geben Sie einen Titel ein');
      return;
    }

    try {
      // day_of_month ist DB-seitig nullbar (relative Frist), daher hier lokal
      // als number|null zugelassen; monat_offset bleibt NOT NULL, wird also nie
      // null gesetzt. Beim Moduswechsel werden die Felder des jeweils anderen
      // Modus geleert, damit keine Mischzustaende entstehen.
      const updates: Omit<Partial<CustomerIndividualDeadline>, 'day_of_month'> & {
        day_of_month?: number | null;
        bezug_lohnlauf?: number | null;
        bezug_werktage_offset?: number | null;
      } = {
        title: formData.title.trim(),
        description: formData.description?.trim() || undefined,
      };

      if (formType === 'recurring') {
        if (formData.bezugsart === 'relativ') {
          updates.bezug_lohnlauf = formData.bezugLohnlauf;
          updates.bezug_werktage_offset = formData.bezugRichtung === 'am_tag'
            ? 0
            : formData.bezugRichtung === 'danach'
              ? formData.bezugWerktageBetrag
              : -formData.bezugWerktageBetrag;
          // Fester-Tag-Felder leeren
          updates.day_of_month = null;
          updates.monat_offset = 0;
          updates.verschiebung = null;
        } else {
          updates.day_of_month = formData.dayOfMonth;
          updates.monat_offset = formData.monatOffset;
          updates.verschiebung = formData.verschiebung;
          // Bezug-Felder leeren
          updates.bezug_lohnlauf = null;
          updates.bezug_werktage_offset = null;
        }
      } else {
        updates.specific_date = formData.specificDate;
      }

      await customerIndividualDeadlineService.updateDeadline(
        editingDeadlineId,
        updates as Partial<CustomerIndividualDeadline>
      );

      // Reset
      resetFormData();
      setEditingDeadlineId(null);
      
      await loadDeadlines();
      onUpdate?.();
    } catch (error) {
      console.error('Error updating deadline:', error);
      alert('Fehler beim Aktualisieren der Deadline');
    }
  };

  const handleDelete = async (deadlineId: string) => {
    if (!window.confirm('Möchten Sie diese Deadline wirklich löschen?')) return;

    try {
      await customerIndividualDeadlineService.deleteDeadline(deadlineId);
      
      // Falls die gelöschte Deadline gerade bearbeitet wird, Formular schließen
      if (editingDeadlineId === deadlineId) {
        setEditingDeadlineId(null);
        resetFormData();
      }
      
      await loadDeadlines();
      onUpdate?.();
    } catch (error) {
      console.error('Error deleting deadline:', error);
      alert('Fehler beim Löschen der Deadline');
    }
  };

  const formatDeadlineInfo = (deadline: CustomerIndividualDeadline): string => {
    if (!deadline.is_recurring && deadline.specific_date) {
      const date = new Date(deadline.specific_date);
      return `${date.toLocaleDateString('de-DE')}${deadline.completed ? ' ✓' : ''}`;
    }
    
    if (deadline.is_recurring && deadline.bezug_lohnlauf) {
      const offset = deadline.bezug_werktage_offset ?? 0;
      const anker = deadline.bezug_lohnlauf === 1 ? '1. Lohnlauf' : '2. Lohnlauf';
      if (offset === 0) {
        return `am ${anker}`;
      }
      const betrag = Math.abs(offset);
      const richtung = offset < 0 ? 'vor' : 'nach';
      return `${betrag} Werktag${betrag === 1 ? '' : 'e'} ${richtung} dem ${anker}`;
    }

    if (deadline.is_recurring && deadline.day_of_month) {
      let info = `Tag ${deadline.day_of_month}`;
      if (deadline.monat_offset && deadline.monat_offset !== 0) {
        info += ` (${deadline.monat_offset > 0 ? '+' : ''}${deadline.monat_offset} Monat)`;
      }
      if (deadline.verschiebung) {
        info += ` - ${deadline.verschiebung === 'frueher' ? 'früher' : 'später'}`;
      }
      return info;
    }
    
    return '';
  };

  // Gemeinsames Formular für Erstellen und Bearbeiten
  const renderForm = (isEdit: boolean) => {
    const currentFormType = formType;
    
    return (
      <div style={{
        border: isEdit ? '2px solid #3B82F6' : '1px solid #E5E7EB',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px',
        backgroundColor: isEdit ? '#EFF6FF' : '#F9FAFB'
      }}>
        {isEdit && (
          <div style={{ 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#3B82F6', 
            marginBottom: '12px' 
          }}>
            Deadline bearbeiten
          </div>
        )}

        {/* Deadline-Typ nur bei Erstellen anzeigen, bei Bearbeiten ist der Typ fix */}
        {!isEdit && (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '14px', fontWeight: '500' }}>
              Deadline-Typ:
            </label>
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  value="recurring"
                  checked={currentFormType === 'recurring'}
                  onChange={(e) => setFormType(e.target.value as 'recurring')}
                />
                <RefreshCw size={14} />
                Wiederkehrend
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  value="one-time"
                  checked={currentFormType === 'one-time'}
                  onChange={(e) => setFormType(e.target.value as 'one-time')}
                />
                <Pin size={14} />
                Einmalig
              </label>
            </div>
          </div>
        )}

        {isEdit && (
          <div style={{ 
            fontSize: '12px', 
            color: '#6B7280', 
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            {currentFormType === 'recurring' ? (
              <><RefreshCw size={12} /> Wiederkehrende Deadline</>
            ) : (
              <><Pin size={12} /> Einmalige Deadline</>
            )}
          </div>
        )}

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}>
            Titel *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            placeholder="z.B. SEPA-Mandat anlegen"
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #D1D5DB',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}>
            Beschreibung (wird bei Hover angezeigt)
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="z.B. Wir müssen für die Techniker Krankenkasse ein SEPA-Mandat anlegen"
            rows={3}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #D1D5DB',
              borderRadius: '4px',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </div>

        {currentFormType === 'recurring' ? (
          <>
            {availableLohnlaeufe.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}>
                  Fristart
                </label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="fest"
                      checked={formData.bezugsart === 'fest'}
                      onChange={() => setFormData(prev => ({ ...prev, bezugsart: 'fest' }))}
                    />
                    Fester Tag
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="relativ"
                      checked={formData.bezugsart === 'relativ'}
                      onChange={() => setFormData(prev => ({ ...prev, bezugsart: 'relativ' }))}
                    />
                    Relativ zum Lohnlauf
                  </label>
                </div>
              </div>
            )}

            {formData.bezugsart === 'relativ' ? (
              <div style={{ display: 'grid', gridTemplateColumns: formData.bezugRichtung === 'am_tag' ? '1fr 1fr' : '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}>
                    Bezug *
                  </label>
                  <select
                    value={formData.bezugLohnlauf}
                    onChange={(e) => setFormData(prev => ({ ...prev, bezugLohnlauf: (parseInt(e.target.value) as 1 | 2) }))}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    {availableLohnlaeufe.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>

                {formData.bezugRichtung !== 'am_tag' && (
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}>
                      Werktage *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.bezugWerktageBetrag}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        bezugWerktageBetrag: Math.max(1, parseInt(e.target.value) || 1)
                      }))}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                )}

                <div>
                  <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}>
                    Richtung
                  </label>
                  <select
                    value={formData.bezugRichtung}
                    onChange={(e) => setFormData(prev => ({ ...prev, bezugRichtung: e.target.value as 'davor' | 'danach' | 'am_tag' }))}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="am_tag">am Lohnlauf-Tag</option>
                    <option value="danach">nach dem Lohnlauf</option>
                    <option value="davor">vor dem Lohnlauf</option>
                  </select>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}>
                      Tag des Monats *
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.dayOfMonth}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        dayOfMonth: Math.min(31, Math.max(1, parseInt(e.target.value) || 1))
                      }))}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}>
                      Monat-Versatz
                    </label>
                    <input
                      type="number"
                      value={formData.monatOffset}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        monatOffset: parseInt(e.target.value) || 0
                      }))}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}>
                    Wochenend-Verschiebung
                  </label>
                  <select
                    value={formData.verschiebung || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      verschiebung: e.target.value as 'frueher' | 'spaeter' | null || null
                    }))}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Keine</option>
                    <option value="frueher">Früher (Freitag)</option>
                    <option value="spaeter">Später (Montag)</option>
                  </select>
                </div>
              </>
            )}
          </>
        ) : (
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px' }}>
              Datum *
            </label>
            <input
              type="date"
              value={formData.specificDate}
              onChange={(e) => setFormData(prev => ({ ...prev, specificDate: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #D1D5DB',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => {
              if (isEdit) {
                setEditingDeadlineId(null);
              } else {
                setShowAddForm(false);
              }
              resetFormData();
            }}
            style={{
              padding: '6px 12px',
              backgroundColor: '#F3F4F6',
              color: '#374151',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={isEdit ? handleUpdate : handleSubmit}
            style={{
              padding: '6px 12px',
              backgroundColor: isEdit ? '#059669' : '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {isEdit ? 'Aktualisieren' : 'Speichern'}
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Lade Deadlines...</div>;
  }

  return (
    <div style={{ marginTop: '20px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
          Individuelle Deadlines
        </h3>
        {!showAddForm && !editingDeadlineId && (
          <button
            onClick={() => setShowAddForm(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 12px',
              backgroundColor: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            <Plus size={16} />
            Deadline hinzufügen
          </button>
        )}
      </div>

      {showAddForm && renderForm(false)}

      {deadlines.length === 0 ? (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#F9FAFB',
          borderRadius: '8px',
          color: '#6B7280',
          fontSize: '14px'
        }}>
          Keine individuellen Deadlines definiert
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {deadlines.map(deadline => (
            <React.Fragment key={deadline.id}>
              {editingDeadlineId === deadline.id ? (
                renderForm(true)
              ) : (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    backgroundColor: '#FFFFFF',
                    border: deadline.is_recurring ? '1px solid #E5E7EB' : '2px dashed #9CA3AF',
                    borderRadius: '6px'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      marginBottom: '4px'
                    }}>
                      {deadline.is_recurring ? (
                        <RefreshCw size={14} color="#6B7280" />
                      ) : (
                        <Pin size={14} color="#6B7280" />
                      )}
                      <span style={{ fontWeight: '500', fontSize: '14px' }}>
                        {deadline.title}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>
                      {formatDeadlineInfo(deadline)}
                    </div>
                    {deadline.description && (
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#9CA3AF',
                        marginTop: '4px',
                        fontStyle: 'italic'
                      }}>
                        {deadline.description}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={() => handleEdit(deadline)}
                      style={{
                        padding: '4px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#3B82F6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Bearbeiten"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(deadline.id)}
                      style={{
                        padding: '4px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#EF4444',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Löschen"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};
